<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.overall_turnover_ratio }}x
            </div>
            <div class="caption">Period Turnover Ratio</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="teal darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.overall_annualized_turnover }}x
            </div>
            <div class="caption">Annualized Turnover</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="green darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_revenue) }}
            </div>
            <div class="caption">Total Revenue</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="purple darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_gross_profit) }}
            </div>
            <div class="caption">Gross Profit</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Secondary Summary -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="4">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h6 font-weight-bold">
              {{ formatCurrency(report.grand_total_cogs) }}
            </div>
            <div class="caption grey--text">Cost of Goods Sold</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h6 font-weight-bold">
              {{ formatCurrency(report.grand_total_avg_inventory) }}
            </div>
            <div class="caption grey--text">Avg Inventory Value</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h6 font-weight-bold">
              {{ report.period_days }} days
            </div>
            <div class="caption grey--text">
              {{ report.from_date }} - {{ report.to_date }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="flatItems"
        :headers="headers"
        :search="search"
        item-key="id"
        dense
        group-by="item_type"
        :items-per-page="50"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search items..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:group.header="{ group, headers, toggle, isOpen }">
          <td
            :colspan="headers.length"
            class="primary white--text pa-2 font-weight-bold"
            style="cursor: pointer"
            @click="toggle"
          >
            <v-icon dark small class="mr-2">{{
              isOpen ? "mdi-minus" : "mdi-plus"
            }}</v-icon>
            {{ group || "Uncategorized" }}
            <span class="float-right mr-4">
              COGS: {{ formatCurrency(getGroupTotal(group, "subtotal_cogs")) }}
              | Revenue:
              {{ formatCurrency(getGroupTotal(group, "subtotal_revenue")) }}
              | Turnover:
              {{ getGroupTotal(group, "group_turnover_ratio") }}x
            </span>
          </td>
        </template>
        <template v-slot:item.cogs="{ item }">
          {{ formatCurrency(item.cogs) }}
        </template>
        <template v-slot:item.revenue="{ item }">
          {{ formatCurrency(item.revenue) }}
        </template>
        <template v-slot:item.gross_profit="{ item }">
          <span :class="item.gross_profit >= 0 ? 'green--text' : 'red--text'">
            {{ formatCurrency(item.gross_profit) }}
          </span>
        </template>
        <template v-slot:item.gross_margin_pct="{ item }">
          <v-chip
            :color="
              item.gross_margin_pct >= 20
                ? 'green'
                : item.gross_margin_pct >= 10
                ? 'orange'
                : 'red'
            "
            dark
            small
          >
            {{ item.gross_margin_pct }}%
          </v-chip>
        </template>
        <template v-slot:item.turnover_ratio="{ item }">
          <v-chip :color="getTurnoverColor(item.turnover_ratio)" dark small>
            {{ item.turnover_ratio }}x
          </v-chip>
        </template>
        <template v-slot:item.annualized_turnover="{ item }">
          {{ item.annualized_turnover }}x
        </template>
        <template v-slot:item.days_to_sell="{ item }">
          <span
            :class="
              item.days_to_sell >= 999
                ? 'grey--text'
                : item.days_to_sell > 90
                ? 'red--text'
                : ''
            "
          >
            {{ item.days_to_sell >= 999 ? "999+" : item.days_to_sell }}
          </span>
        </template>
        <template v-slot:item.current_inventory_value="{ item }">
          {{ formatCurrency(item.current_inventory_value) }}
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-refresh</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to calculate inventory turnover ratios.
        </p>
        <p class="caption grey--text">
          Turnover ratio = Cost of Goods Sold / Average Inventory Value. Higher
          is better.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "InventoryTurnoverReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      headers: [
        { text: "Item Name", value: "item_name" },
        { text: "Supplier", value: "supplier" },
        { text: "Batch", value: "batch_name" },
        { text: "Stock", value: "current_stock", align: "center" },
        { text: "Qty Sold", value: "qty_sold", align: "center" },
        { text: "COGS", value: "cogs", align: "end" },
        { text: "Revenue", value: "revenue", align: "end" },
        { text: "Gross Profit", value: "gross_profit", align: "end" },
        { text: "Margin %", value: "gross_margin_pct", align: "center" },
        { text: "Inv. Value", value: "current_inventory_value", align: "end" },
        { text: "Turnover", value: "turnover_ratio", align: "center" },
        { text: "Annual", value: "annualized_turnover", align: "center" },
        { text: "Days to Sell", value: "days_to_sell", align: "center" },
      ],
    };
  },
  computed: {
    flatItems() {
      if (!this.report || !this.report.groups) return [];
      let items = [];
      this.report.groups.forEach((group) => {
        group.items.forEach((item) => {
          items.push({ ...item, item_type: group.item_type });
        });
      });
      return items;
    },
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_turnover_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getTurnoverColor(ratio) {
      if (ratio >= 2) return "green darken-1";
      if (ratio >= 1) return "teal";
      if (ratio >= 0.5) return "orange";
      if (ratio > 0) return "amber darken-2";
      return "grey";
    },
    getGroupTotal(groupName, field) {
      if (!this.report || !this.report.groups) return 0;
      let group = this.report.groups.find((g) => g.item_type === groupName);
      if (!group) return 0;
      return group[field] || 0;
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating turnover report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
